import Vue from "vue";
import Vuex from "vuex";
import * as api from "@/api";
import { setToken, removeToken } from "@/api/helpers";
import customSimulationModule from "./modules/custom-simulation";
import modelViewModule from "./modules/model-view";
import simulationRightMenuModule from "./modules/simulation-resource-model-view-right-menu";

let userFromLocalStorage: api.User | null = null;
let compareListFromLocalStorage: any[] = [];

try {
    userFromLocalStorage = JSON.parse(localStorage.getItem("user") || "null");
    compareListFromLocalStorage = JSON.parse(localStorage.getItem("compareList") || "[]");
} catch (err) {
    userFromLocalStorage = null;
    compareListFromLocalStorage = [];
}

type Function = (any) => void;

Vue.use(Vuex);
export default new Vuex.Store({
    modules: { customSimulationModule, modelViewModule, simulationRightMenuModule },
    state: {
        /**
         * 相关产品信息
         */
        userSummary: {
            // 用户信息概览
            brandCount: 0,
            collectedCount: 0,
            downloadedCount: 0,
            toBeUpdatedCount: 0
        },
        relatedProductInfo: sessionStorage.getItem("relatedProductInfo")
            ? JSON.parse(sessionStorage.getItem("relatedProductInfo") as string)
            : null,
        user: userFromLocalStorage,
        compareList: compareListFromLocalStorage,
        compareProductList: [] as api.CompareList[], // 用户对比产品列表
        searchKeyword: "",
        clickGroupIds: [],
        modelProgress: 0,
        showSearchBar: true, // 控制header上的搜索框显示
        mainSearchbar: true, // 控制主搜索框的显示
        modelLoadComplete: false,
        reloadVs: false,
        loginVisible: false,
        bindVisible: false,
        currentProductInfo: sessionStorage.getItem("currentProductInfo")
            ? JSON.parse(sessionStorage.getItem("currentProductInfo") as string)
            : {},
        unreadCount: 0, // 未读消息数
        lookNewsId: "", // 查看消息
        isMobile: window.innerWidth <= 1120,
        /**
         * 依赖用户登录状态而失败的接口将放在这里
         * 做本地持久化
         */
        failAjaxQueue: JSON.parse(sessionStorage.getItem("failAjaxQueue") || "[]") as
            | { handle: Function; arguments: any }[]
            | [],
        sessionId: ""
    },
    getters: {
        compareList(state) {
            console.log(state.compareList);
        }
    },
    mutations: {
        compareProductList(state, compareProductList) {
            state.compareProductList = compareProductList;
        },
        userSummary(state, userSummary) {
            state.userSummary = userSummary;
        },
        relatedProductInfo(state, relatedProductInfo) {
            state.relatedProductInfo = relatedProductInfo;
            sessionStorage.setItem("relatedProductInfo", JSON.stringify(relatedProductInfo));
        },
        lookNewsId(state, lookNewsId) {
            state.lookNewsId = lookNewsId;
        },
        failAjaxQueue(state, ajax: { handle: Function; arguments: any }[] | []) {
            state.failAjaxQueue = ajax;
            sessionStorage.setItem("failAjaxQueue", JSON.stringify(ajax));
        },
        unreadCount(state, unreadCount) {
            state.unreadCount = unreadCount;
        },
        currentProductInfo(state, currentProductInfo) {
            state.currentProductInfo = currentProductInfo;
            sessionStorage.setItem("currentProductInfo", JSON.stringify(currentProductInfo));
        },
        bindVisible(state, bindVisible) {
            state.bindVisible = bindVisible;
        },
        loginVisible(state, loginVisible) {
            state.loginVisible = loginVisible;
            if (loginVisible) {
                /**
                 * 当登录窗口打开时做两件事情
                 * 1.清空用户的信息
                 * 2.清空对比列表
                 */
                state.user = null;
                localStorage.removeItem("user");
                state.compareList = [];
                localStorage.setItem("compareList", JSON.stringify(state.compareList));
            }
        },
        setUser(state, user) {
            if (user && !user.avatar) {
                user.avatar = "/cdn/images/default/avatar.png";
            }
            state.user = user;
            localStorage.setItem("user", JSON.stringify(user));
            window.location.reload(); // 登录过期重新登陆后刷新页面
        },
        logout(state) {
            state.user = null;
            localStorage.removeItem("user");
            removeToken();
        },
        addCompare(state, product: api.ProductCard) {
            if (state.compareList.length >= 3 || state.compareList.includes(product)) {
                return;
            }
            state.compareList.push(product);
            localStorage.setItem("compareList", JSON.stringify(state.compareList));
        },
        removeCompare(state, product: api.ProductCard) {
            if (state.compareList.length === 0) {
                return;
            }
            const idx = state.compareList.findIndex(p => p.id === product.id);
            if (idx !== -1) {
                state.compareList.splice(idx, 1);
                localStorage.setItem("compareList", JSON.stringify(state.compareList));
            }
        },
        clearCompare(state) {
            state.compareList = [];
            localStorage.setItem("compareList", JSON.stringify(state.compareList));
        },
        searchKeyword(state, val) {
            state.searchKeyword = val;
        },
        modelProgress(state, val) {
            state.modelProgress = val;
        },
        modelLoadComplete(state, val) {
            state.modelLoadComplete = val;
        },
        clickGroupIds(state, val) {
            state.clickGroupIds = val;
        },
        reloadVs(state, val) {
            state.reloadVs = val;
        },
        showSearchBar(state) {
            state.showSearchBar = true;
        },
        hideSearchBar(state) {
            state.showSearchBar = false;
        },
        mainSearchbar(state, o) {
            state.mainSearchbar = o;
        },
        setSessionId(state, payload) {
            state.sessionId = payload;
        }
    },
    actions: {
        async getProductCompareList({ commit }) {
            const res = await api.getProductCompareList();
            if (!api.error(res)) {
                commit("compareProductList", res.data);
            }
        },
        async putProductCompare({ dispatch }, data: { productId: string; pvId: string }) {
            const res = await api.putProductCompare({
                productId: data.productId,
                pvId: data.pvId
            });
            if (!api.error(res)) {
                dispatch("getProductCompareList");
            }
        },
        async deleteAllProductCompare({ dispatch }) {
            const res = await api.deleteAllProductCompare();
            if (!api.error(res)) {
                dispatch("getProductCompareList");
            }
        },
        async deleteProductCompareById({ dispatch }, productId: string) {
            const res = await api.deleteProductCompareById({ productId });
            if (!api.error(res)) {
                dispatch("getProductCompareList");
            }
        },
        async getUserSummary({ commit }) {
            const res = await api.getUserSummary();
            if (!api.error(res)) {
                commit("userSummary", res.data);
            }
        },
        loginHelper({ commit }, ajax: Function) {
            commit("loginVisible", true);
            commit("failAjaxQueue", ajax);
        },
        async reRunFailAjax({ state, commit }) {
            if (state.failAjaxQueue.length > 0) {
                console.log(state.failAjaxQueue[0]);
                const res = state.failAjaxQueue[0].handle(state.failAjaxQueue[0].arguments);
                if (!api.error(res)) {
                    commit("failAjaxQueue", []);
                }
            }
        },
        setToken({ dispatch }, token) {
            setToken(token);
            dispatch("fetchUserInfo");
        },
        async fetchUserInfo({ commit, dispatch }) {
            dispatch("reRunFailAjax");
            const res = await api.getUser();
            if (!api.error(res)) {
                if (!res.data.phoneNumber) {
                    commit("bindVisible", true);
                } else {
                    commit("setUser", res.data);
                }
            }
        },
        async fetchUserInfoToCheckPhone({ commit, dispatch }) {
            dispatch("reRunFailAjax");
            const res = await api.getUser();
            if (!api.error(res)) {
                commit("setUser", res.data);
            }
        },
        async getUnreadCount({ commit }) {
            const res = await api.getUnreadCount();
            if (!api.error(res)) {
                commit("unreadCount", res.data);
            }
        },
        async getSessionId({ commit }) {
            // 获取spd token
            const res = await api.getSessionId();
            if (!api.error(res)) {
                commit("setSessionId", res.data);
            }
        }
    }
});
